import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../AuthProvider";
import { useWorkService } from "../../services/work.service";
import { WorkDto, ValidationForm, WorkStatus, WorkField, RegisteredVoterFlags } from "../../types/work.types";
import { Roles } from "../../types/user.types";
import { FormikProps } from "formik";
import { handleError, convertFieldValuesToStrings, convertFieldValuesToTypes, checkForRole } from "../../common/utils";
import WorkItem from "../../components/work-item.component";
import ValidationWarningDialog from "../../components/validation-warning-dialog.component";
import { useWorkValidation } from "../../hooks/useWorkValidation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { Loader } from "../../components/loader";
import HeaderNavBar from "../../components/header-nav-bar";
import WorkBreakResume from "../../components/work-break-resume.component";

export default function FlaggedWork() {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { authenticatedUser } = useAuthContext();
  const { getNextFlaggedWork } = useWorkService();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [work, setWork] = useState<WorkDto | null>();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [previousPage, setPreviousPage] = useState("");
  const [backButtonText, setBackButtonText] = useState("");
  const formikRef = useRef<FormikProps<ValidationForm>>(null);

  const {
    submitWorkIfDirty,
    handleValidationOverride,
    handleValidationCancel,
    showValidationWarning,
    validationWarningMessage,
  } = useWorkValidation({
    setLoading,
    onError: setErrorMessage,
  });

  const getPreviousPage = useCallback(() => {
    const from = location.pathname.split("/")[1];
    if (from === "admin") {
      const matterId = params.matterId;
      if (matterId) {
        setPreviousPage(`/admin/matters/${matterId}/details`);
        setBackButtonText(`Back to Matter`);
      }
    } else {
      setPreviousPage("/manager");
      setBackButtonText(`Back to Manager Dashboard`);
    }
  }, [location.pathname, params.matterId]);

  const getAdjustSheetUrl = () => {
    const from = location.pathname.split("/")[1];
    return `/${from}/matters/${work?.matterId}/sheets/${work?.sheetNumber}?workId=${work?.workId}`;
  };

  const loadNextFlaggedWork = useCallback(() => {
    const matterId = params.matterId;
    if (!matterId) {
      return;
    }
    setErrorMessage("");
    setLoading(true);
    setWork(null);
    getNextFlaggedWork(matterId).then(
      (work) => {
        setLoading(false);
        if (work) {
          setIsInitialLoad(false);
          work.fields = convertFieldValuesToStrings(work.fields);
          setWork(work);
          const from = location.pathname.split("/")[1];
          if (from === "admin") {
            navigate(`/admin/matters/${matterId}/review/transcription/${work?.workId}`);
          } else {
            navigate(`/manager/${matterId}/flagged/${work?.workId}`);
          }
        } else {
          // All work completed
          navigate(previousPage);
        }
      },
      (error) => {
        handleError(error, setErrorMessage, setLoading);
      }
    );
  }, [getNextFlaggedWork, location.pathname, navigate, params.matterId, previousPage]);

  useEffect(() => {
    if (!checkForRole(Roles.Manager, authenticatedUser?.roleId)) {
      navigate("/login");
    } else if (!work && isInitialLoad) {
      getPreviousPage();
      loadNextFlaggedWork();
    }
  }, [authenticatedUser?.roleId, getPreviousPage, isInitialLoad, loadNextFlaggedWork, navigate, work]);

  const updateWorkItem = async (
    fields: WorkField[],
    workStatus: WorkStatus,
    externalDataRecordId?: string | null,
    registeredVoterFlags?: RegisteredVoterFlags | null,
    searchParams?: Record<string, string>
  ): Promise<void> => {
    if (!work?.workId) {
      return Promise.reject("No work");
    }

    const workData = {
      workStatusId: workStatus,
      fields: convertFieldValuesToTypes(fields),
      externalDataRecordId: externalDataRecordId || null,
      registeredVoterFlags: registeredVoterFlags || RegisteredVoterFlags.Unknown,
      searchParams,
    };

    await submitWorkIfDirty(work.workId.toString(), workData, formikRef);
    setWork({ ...work, fields: convertFieldValuesToStrings(fields) });
  };

  const handleSaveWork = (
    fields: WorkField[],
    externalDataRecordId?: string | null,
    registeredVoterFlags?: RegisteredVoterFlags | null,
    searchParams?: Record<string, string>
  ): Promise<void> => {
    return updateWorkItem(fields, WorkStatus.Completed, externalDataRecordId, registeredVoterFlags, searchParams);
  };

  const handleTakeBreak = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (formikRef.current) {
      const formValues = formikRef.current.values;
      updateWorkItem(
        work?.fields || [],
        WorkStatus.Break,
        formValues.externalDataRecordId,
        formValues.registeredVoterFlags,
        formValues.searchParams
      ).then(() => {
        setIsOnBreak(true);
        setWork(null);
      });
    }
  };

  const handleResume = () => {
    setIsOnBreak(false);
    loadNextFlaggedWork();
  };

  const handleNextFlaggedWork = (
    fields: WorkField[],
    externalDataRecordId?: string | null,
    registeredVoterFlags?: RegisteredVoterFlags | null
  ) => {
    if (work?.workId) {
      handleSaveWork(fields, externalDataRecordId, registeredVoterFlags).then(
        () => {
          loadNextFlaggedWork();
        },
        (error) => {
          handleError(error, setErrorMessage, setLoading);
        }
      );
    }
  };

  return (
    <div className="container">
      <HeaderNavBar
        leftContent={
          <Link to={previousPage} className="btn btn-sw btn-sm btn-outline">
            <FontAwesomeIcon icon={faCircleLeft} className="me-2" />
            {backButtonText}
          </Link>
        }
        rightContent={
          <Link to={getAdjustSheetUrl()} className="btn btn-sw btn-sm btn-outline">
            Adjust Sheet
          </Link>
        }
      />

      {loading && <Loader />}

      {!isOnBreak && !loading && work && (
        <WorkItem
          work={work}
          onSave={handleSaveWork}
          onNext={handleNextFlaggedWork}
          onTakeBreak={handleTakeBreak}
          formikRef={formikRef}
          errorMessage={errorMessage}
        />
      )}

      {isOnBreak && <WorkBreakResume isOnBreak={isOnBreak} loading={loading} onResume={handleResume} />}

      {errorMessage && (
        <div className="row">
          <div className="col">
            <div className="form-group">
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            </div>
          </div>
        </div>
      )}

      <ValidationWarningDialog
        show={showValidationWarning}
        errorMessage={validationWarningMessage}
        onCancel={handleValidationCancel}
        onOverride={handleValidationOverride}
      />
    </div>
  );
}
